import React, { useState, useEffect, ReactNode } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Toolbar,
  Typography,
  Divider,
  Box,
  Switch,
  InputBase,
  Tooltip,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
// @ts-ignore - image import handled by bundler
import logo from "../../assets/images/inventory.png";
import { useSelector } from "react-redux";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTheme } from "@mui/material/styles";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";

// Type definitions
interface SidebarProps {
  handleDrawerToggle: () => void;
  desktopOpen: boolean;
  toggleTheme: () => void;
}

interface AccessRightItem {
  MODULECODE?: string;
  MAINMENU: string;
  MAINMENUORDER: number;
  SUBMENU?: string;
  SUBMENUORDER: number;
  MODMENUNAME?: string;
  URL: string;
  ICONPATH?: string;
}

interface MenuItem {
  text: string;
  path: string;
  order: number;
  icon: ReactNode;
}

interface GroupedMenu {
  MAINMENUORDER: number;
  items: MenuItem[];
}

interface UserParam {
  COMPANYLOGO?: string;
  COMPCODE?: string;
  COMPNAME?: string;
  USERREFCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParam;
}

interface RootState {
  user: UserState;
  accessRights: AccessRightItem[] | Record<string, AccessRightItem>;
}

const drawerWidth = 240;
const collapsedWidth = 0;

const Sidebar: React.FC<SidebarProps> = ({ handleDrawerToggle, desktopOpen, toggleTheme }) => {
  const theme = useTheme();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState<string>(location.pathname);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const accessRights = useSelector((state: RootState) => state.accessRights);

  const handleTabClick = (path: string) => {
    setSelectedTab(path);
  };

  const handleSubMenuClick = (menu: string) => {
    setOpenSubMenu(openSubMenu === menu ? null : menu);
  };

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state: RootState) => state.user) || {};
  const { COMPANYLOGO, COMPNAME } = SYS_USER.USER_PARAM || {};

  // Transform the API response to an array
  const accessRightsArray: AccessRightItem[] = Array.isArray(accessRights)
    ? accessRights
    : Object.values(accessRights).filter(
        (item): item is AccessRightItem => item.MODULECODE !== undefined
      );

  useEffect(() => {}, [accessRightsArray]);

  // Transform the data into grouped menu items
  const groupedAccessRights = accessRightsArray.reduce<Record<string, GroupedMenu>>((acc, curr) => {
    const {
      MAINMENU,
      MAINMENUORDER,
      SUBMENU,
      SUBMENUORDER,
      MODMENUNAME,
      URL,
      ICONPATH,
    } = curr;

    if (!acc[MAINMENU]) {
      acc[MAINMENU] = {
        MAINMENUORDER,
        items: [],
      };
    }

    acc[MAINMENU].items.push({
      text: SUBMENU || MODMENUNAME || '',
      path: String(URL),
      order: SUBMENUORDER,
      icon: ICONPATH ? (
        <img
          src={ICONPATH}
          alt={SUBMENU || MODMENUNAME}
          style={{ width: 20, height: 20 }}
        />
      ) : (
        <FiberManualRecordOutlinedIcon sx={{ fontSize: "10px" }} />
      ),
    });

    return acc;
  }, {});

  // Sort the grouped menus by MAINMENUORDER
  const sortedGroupedAccessRights = Object.entries(groupedAccessRights).sort(
    (a, b) => a[1].MAINMENUORDER - b[1].MAINMENUORDER
  );

  const drawer = (
    <Box
      sx={{
        height: "100vh",
        background: theme.palette.background.paper,
        px: 1,
        borderRight: "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: desktopOpen ? "center" : "center",
          p: 2,
          position: "fixed",
          top: 0,
        }}
      >
        {desktopOpen ? (
          <>
            <img
              src={COMPANYLOGO || logo}
              alt="Logo"
              style={{
                width: 40,
                height: 40,
                marginRight: "16px",
              }}
            />
            <Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title={COMPNAME || ""} arrow>
                  <Typography
                    variant="h5"
                    sx={{
                      display: "flex",
                      maxWidth: 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {COMPNAME}
                  </Typography>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  border: 1,
                  textAlign: "center",
                  borderRadius: 1,
                  maxWidth: 40,
                  maxHeight: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="body_xxs">V1.1</Typography>
              </Box>{" "}
            </Box>
          </>
        ) : (
          <img
            src={COMPANYLOGO || logo}
            alt="Logo"
            style={{ width: 40, height: 40 }}
          />
        )}
      </Toolbar>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          mt: 12,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box width={"100%"} px={1}>
          <InputBase
            placeholder="Ctrl + K"
            sx={{
              backgroundColor: theme.palette.background.default,
              borderRadius: 1,
              padding: "0 10px",
              height: "36px",
              display: "flex",
            }}
            startAdornment={<SearchIcon sx={{ color: "gray", mr: 1 }} />}
          />
        </Box>
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexGrow: 1,
          }}
        >
          {sortedGroupedAccessRights.map(([mainMenu, { items }]) => (
            <React.Fragment key={mainMenu}>
              <ListItem
                disablePadding
                sx={{
                  transition: "all 0.3s ease-in-out",
                }}
              >
                <ListItemButton
                  onClick={() => handleSubMenuClick(mainMenu)}
                  sx={{
                    transition: "all 0.3s ease-in-out",
                    color:
                      selectedTab === mainMenu
                        ? theme.palette.mode === "light"
                          ? "#000000"
                          : "#FFFFFF"
                        : theme.palette.text.primary,
                    boxShadow:
                      selectedTab === mainMenu
                        ? `0px 1px 2px rgba(0, 0, 0, 0.1)`
                        : 0,
                    backgroundColor:
                      selectedTab === mainMenu
                        ? theme.palette.mode === "light"
                          ? "#064291"
                          : "#0F61AC"
                        : "inherit",
                    "&:hover": {
                      backgroundColor:
                        selectedTab === mainMenu
                          ? theme.palette.mode === "light"
                            ? "#064291"
                            : "#0F61AC"
                          : theme.palette.tertiary.main,
                      color:
                        selectedTab === mainMenu
                          ? theme.palette.mode === "light"
                            ? "#000000"
                            : "#FFFFFF"
                          : theme.palette.primary.main,
                      borderRight:
                        selectedTab === mainMenu
                          ? "none"
                          : `3px solid ${theme.palette.secondary.main}`,
                    },
                    justifyContent: desktopOpen ? "flex-start" : "center",
                    paddingLeft: desktopOpen ? "12px" : "12px",
                    gap: 2,
                    paddingRight: 1,
                  }}
                >
                  {desktopOpen && (
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: theme.typography.body_xs as string | number,
                      }}
                      primary={mainMenu}
                    />
                  )}
                  {items.length > 0 &&
                    desktopOpen &&
                    (openSubMenu === mainMenu ? (
                      <ExpandLess
                        sx={{
                          color: theme.palette.text.secondary,
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          color: theme.palette.text.secondary,
                        }}
                      />
                    ))}
                </ListItemButton>
              </ListItem>
              <Collapse
                in={openSubMenu === mainMenu}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {items
                    .sort((a, b) => a.order - b.order)
                    .map(({ text, path, icon }) => (
                      <ListItem
                        key={text}
                        disablePadding
                        sx={{
                          transition: "all 0.3s ease-in-out",
                        }}
                      >
                        <ListItemButton
                          component={Link}
                          to={path}
                          onClick={() => handleTabClick(path)}
                          selected={selectedTab === path}
                          sx={{
                            transition: "all 0.3s ease-in-out",
                            color:
                              selectedTab === path
                                ? theme.palette.mode === "light"
                                  ? "#000000"
                                  : "#FFFFFF"
                                : theme.palette.text.primary,
                            boxShadow:
                              selectedTab === path
                                ? `0px 1px 2px rgba(0, 0, 0, 0.1)`
                                : 0,
                            backgroundColor:
                              selectedTab === path
                                ? theme.palette.mode === "light"
                                  ? "#1677FF"
                                  : "#0F61AC"
                                : "inherit",
                            "&.Mui-selected": {
                              backgroundColor:
                                theme.palette.mode === "light"
                                  ? "#1677FF"
                                  : "#0F61AC",
                              color:
                                theme.palette.mode === "light"
                                  ? "#000000"
                                  : "#FFFFFF",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.mode === "light"
                                    ? "#1677FF"
                                    : "#0F61AC",
                                color:
                                  theme.palette.mode === "light"
                                    ? "#000000"
                                    : "#FFFFFF",
                              },
                            },
                            "&:hover:not(.Mui-selected)": {
                              backgroundColor: theme.palette.tertiary.main,
                              color: theme.palette.primary.main,
                              borderRight: `3px solid ${theme.palette.secondary.main}`,
                            },
                            justifyContent: desktopOpen ? "flex-start" : "center",
                            paddingLeft: desktopOpen ? "24px" : "12px",
                            gap: 2,
                            paddingRight: 1,
                          }}
                        >
                          {
                            <FiberManualRecordOutlinedIcon
                              sx={{
                                fontSize: "10px",
                                color: selectedTab === path
                                  ? theme.palette.mode === "light"
                                    ? "#000000"
                                    : "#FFFFFF"
                                  : theme.palette.text.secondary,
                                marginRight: desktopOpen ? "8px" : "0px",
                                transition: "all 0.3s ease-in-out",
                              }}
                            />
                          }
                          {desktopOpen && (
                            <ListItemText
                              primaryTypographyProps={{
                                fontSize: theme.typography.body_xs as string | number,
                              }}
                              primary={text}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Box>
      <Divider />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
          px: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "8px" }}>
          {theme.palette.mode === "dark" ? (
            <DarkModeIcon sx={{ fontSize: "20px" }} />
          ) : (
            <LightModeIcon sx={{ fontSize: "20px" }} />
          )}

          <Typography
            sx={{
              fontSize: theme.typography.subtitle_xs as string | number,
              fontWeight: 500,
              marginLeft: "8px",
              color: "#6b7280",
            }}
          >
            {theme.palette.mode === "dark" ? "Dark Mode" : "Light Mode"}
          </Typography>
        </Box>
        <Box>
          <Switch
            checked={theme.palette.mode === "dark"}
            onChange={toggleTheme}
            sx={{
              width: 56,
              height: 30,
              padding: 0,
              borderRadius: 12,
              "& .MuiSwitch-switchBase": {
                transitionDuration: "300ms",
                "&.Mui-checked": {
                  transform: "translateX(28px)",
                  color: "#FFA500",
                  "& + .MuiSwitch-track": {
                    opacity: 1,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                  },
                },
              },
              "& .MuiSwitch-thumb": {
                backgroundColor: theme.palette.primary.main,
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                width: 16,
                height: 16,
                transition: "all 0.3s ease-in-out",
                position: "relative",
                "&:before": {
                  content: '""',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "12px",
                },
              },
              "& .MuiSwitch-track": {
                borderRadius: 12,
                opacity: 0.6,
                backgroundColor: theme.palette.background.paper,
                transition: theme.transitions.create(["background-color"], {
                  duration: 500,
                }),
                boxShadow: "inset 0px 2px 10px rgba(0, 0, 0, 0.2)",
              },
            }}
            icon={
              <LightModeIcon
                style={{
                  fontSize: "14px",
                  color: theme.palette.greyscale.icons,
                }}
              />
            }
            checkedIcon={
              <DarkModeIcon
                style={{
                  fontSize: "14px",
                  color: theme.palette.primary.contrastText,
                }}
              />
            }
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: desktopOpen ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: desktopOpen ? drawerWidth : collapsedWidth,
            transition: "width 0.3s ease-in-out",
            borderRight: "none",
            overflowX: "hidden",
            backgroundColor: theme.palette.background.paper,
            boxShadow: `1px 0px 1px rgba(0, 0, 0, 0.1)`,
          },
        }}
        open
      >
        {drawer}
      </Drawer>{" "}
    </>
  );
};

export default Sidebar;
